<template>
    <div class="excel-file-upload">
        <div
            class="upload-area"
            :class="{ 'is-dragging': isDragging, 'has-file': selectedFile, 'is-loading': isLoading }"
            @drop.prevent="handleDrop"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @click="!isLoading && triggerFileInput()"
        >
            <input ref="fileInput" type="file" accept=".xlsx,.xls" class="hidden" @change="handleFileSelect" />

            <div v-if="!selectedFile && !isLoading" class="upload-prompt">
                <i class="fa-solid fa-file-excel text-5xl text-green-600"></i>
                <p class="mt-4 text-lg font-medium text-gray-700">
                    {{ isDragging ? 'Drop your Excel file here' : 'Drag and drop or click to select' }}
                </p>
                <p class="mt-2 text-sm text-gray-500">Supports .xlsx and .xls files</p>
            </div>

            <div v-else-if="isLoading" class="loading-state">
                <n-spin size="large" />
                <p class="mt-4 text-lg font-medium text-gray-700">Uploading...</p>
            </div>

            <div v-else-if="selectedFile" class="file-selected">
                <i class="fa-solid fa-file-excel text-4xl text-green-600"></i>
                <p class="mt-3 text-base font-medium text-gray-700">{{ selectedFile.name }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
            </div>
        </div>

        <!-- Result Display -->
        <div v-if="result" class="result-section mt-4">
            <div v-if="result.status === 'SUCCESS'" class="success-message">
                <i class="fa-solid fa-check-circle text-2xl text-green-600"></i>
                <div class="ml-3">
                    <h3 class="text-base font-semibold text-green-800">Import Successful!</h3>
                    <p class="mt-1 text-sm text-green-700">{{ result.positionsImported }} positions imported successfully.</p>
                </div>
            </div>

            <div v-else-if="result.status === 'FAILED'" class="error-message">
                <i class="fa-solid fa-exclamation-triangle text-2xl text-red-600"></i>
                <div class="ml-3 flex-1">
                    <h3 class="text-base font-semibold text-red-800">Upload Failed</h3>
                    <div v-if="result.errors && result.errors.length > 0" class="mt-2">
                        <ul class="space-y-1">
                            <li v-for="(error, index) in result.errors" :key="index" class="text-sm text-red-700">• {{ error }}</li>
                        </ul>
                    </div>
                    <p v-else class="mt-2 text-sm text-red-700">An error occurred during the upload. Please check the file format and try again.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { NSpin } from 'naive-ui';
import { useMfhRenovationProjectMutations } from '@/hooks';

interface Props {
    tenderingProcessId: number;
    tenderingProcessTendererId: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    uploadComplete: [result: { status: 'SUCCESS' | 'FAILED'; positionsImported?: number; errors?: string[] }];
}>();

const { importTenderingProcessOffers } = useMfhRenovationProjectMutations();

const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const isLoading = ref(false);
const result = ref<{ status: 'SUCCESS' | 'FAILED'; positionsImported?: number; errors?: string[] } | null>(null);

const triggerFileInput = () => {
    fileInput.value?.click();
};

const handleFileSelect = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file && isExcelFile(file)) {
        selectedFile.value = file;
        uploadFile();
    }
};

const handleDrop = (event: DragEvent) => {
    isDragging.value = false;
    const file = event.dataTransfer?.files[0];
    if (file && isExcelFile(file)) {
        selectedFile.value = file;
        uploadFile();
    }
};

const handleDragOver = () => {
    isDragging.value = true;
};

const handleDragLeave = () => {
    isDragging.value = false;
};

const isExcelFile = (file: File): boolean => {
    const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
    ];
    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    return hasValidType || hasValidExtension;
};

const uploadFile = async () => {
    if (!selectedFile.value) return;

    isLoading.value = true;
    result.value = null;

    try {
        const uploadResult = await importTenderingProcessOffers({
            tenderingProcessId: props.tenderingProcessId,
            tenderingProcessTendererId: props.tenderingProcessTendererId,
            file: selectedFile.value,
        });
        result.value = uploadResult;
        emit('uploadComplete', uploadResult);
    } catch (error) {
        result.value = {
            status: 'FAILED',
            errors: ['An unexpected error occurred during upload. Please try again.'],
        };
        emit('uploadComplete', result.value);
    } finally {
        isLoading.value = false;
    }
};

const removeFile = () => {
    selectedFile.value = null;
    result.value = null;
    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const resetUpload = () => {
    removeFile();
};

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

defineExpose({
    uploadFile,
    removeFile,
    resetUpload,
});
</script>

<style scoped>
.excel-file-upload {
    width: 100%;
}

.upload-area {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 3rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f9fafb;
}

.upload-area:hover:not(.is-loading) {
    border-color: #10b981;
    background-color: #f0fdf4;
}

.upload-area.is-dragging {
    border-color: #10b981;
    background-color: #d1fae5;
}

.upload-area.has-file {
    border-color: #10b981;
    background-color: #ecfdf5;
}

.upload-area.is-loading {
    cursor: not-allowed;
    opacity: 0.7;
}

.hidden {
    display: none;
}

.upload-prompt,
.loading-state,
.file-selected {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.btn-upload,
.btn-remove,
.btn-retry {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
}

.btn-upload {
    background-color: #10b981;
    color: white;
}

.btn-upload:hover {
    background-color: #059669;
}

.btn-remove {
    background-color: #ef4444;
    color: white;
}

.btn-remove:hover {
    background-color: #dc2626;
}

.btn-retry {
    background-color: #3b82f6;
    color: white;
}

.btn-retry:hover {
    background-color: #2563eb;
}

.result-section {
    border-radius: 8px;
    padding: 1rem;
}

.success-message {
    display: flex;
    align-items: start;
    padding: 1rem;
    background-color: #d1fae5;
    border: 1px solid #10b981;
    border-radius: 6px;
}

.error-message {
    display: flex;
    align-items: start;
    padding: 1rem;
    background-color: #fee2e2;
    border: 1px solid #ef4444;
    border-radius: 6px;
}
</style>
